import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useRecognitionCatalog, useRecognitionRules } from '@/api/recognition';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { fundamentalesOfrecibles } from '../postitShared';
import { StepInstructions } from './StepInstructions';
import { StepUploadFile } from './StepUploadFile';
import { StepReviewRows } from './StepReviewRows';
import { StepConfirm } from './StepConfirm';
import type { BulkEditableRow, WizardStep } from './types';

interface Props {
  onClose: () => void;
  /** Campaña heredada del contexto donde se abrió el wizard. `null` = reconocimiento diario. */
  campaignId: string | null;
  /**
   * Programa de la pantalla que abrió el wizard ('Brillamos' desde la pantalla de Brillamos). Viaja al
   * backend para que valide el lote con las reglas del MISMO programa con el que se habilitó el botón:
   * sin él, un lote sin campaña se validaba contra Fundamentales (el doc global) y el preview moría con
   * un 403 aunque Brillamos tuviera la carga masiva encendida. Sin valor ⇒ Fundamentales.
   */
  programa?: string | null;
}

const STEPS: WizardStep[] = ['instructions', 'upload', 'review', 'confirm'];
const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  instructions: 'bulk.stepInstructions',
  upload: 'bulk.stepUpload',
  review: 'bulk.stepReview',
  confirm: 'bulk.stepConfirm',
};

/** Wizard de carga masiva de reconocimientos por CSV (RN-CSV01..04 / REQ-085 / REQ-106). */
export function BulkUploadModal({ onClose, campaignId, programa }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const catalog = useRecognitionCatalog();
  // BUG (Julio, 2026-08-21) — antes solo filtraba por `isActive` (borrado lógico del catálogo de
  // Pulso), e ignoraba `fundamentalesDeshabilitados` (el bloque «Fundamentales disponibles» de
  // Gestión — WP-F5): un Fundamental retirado del programa sin borrarse del catálogo seguía
  // ofreciéndose aquí, y el envío moría con 422 en el servidor. Mismo helper y mismo criterio que ya
  // usa el flujo normal de Reconocer (`ReconocerPanel.tsx`) — un Fundamental nuevo sigue apareciendo
  // solo con estar en el catálogo, sin tocar código, porque el modelo es de EXCLUIDOS.
  const rules = useRecognitionRules(campaignId, programa);
  const fundamentals = fundamentalesOfrecibles(catalog.data, rules.data?.fundamentalesDeshabilitados);
  const [step, setStep] = useState<WizardStep>('instructions');
  const [rows, setRows] = useState<BulkEditableRow[]>([]);
  // Guarda: hay trabajo que perder cuando ya se cargaron filas del CSV para revisar/enviar.
  useUnsavedChangesGuard(rows.length > 0);

  const stepIndex = STEPS.indexOf(step);

  // `createPortal` a `document.body`: reportado por el usuario con el menú lateral expandido — el
  // modal se pintaba por DEBAJO del sidebar en vez de cubrirlo. Causa: este componente se monta
  // dentro de `<main className="plataforma-main">` (recognition-shell.css), que lleva `isolation:
  // isolate` — aísla en su propio contexto de apilamiento TODO lo que haya adentro, `.modal-overlay`
  // (z-index 2000) incluido, así que ese z-index solo compite puertas adentro de `.plataforma-main`
  // y nunca contra el z-index 200, explícito, de `.side-nav` (Sidebar.css), que es hermano de
  // `.plataforma-main` en `.plataforma-shell`. Mismo patrón de fix ya usado en `WinnersImportWizardModal.tsx`
  // (`.adm-section` con backdrop-filter) y en `components/common/Modal/index.tsx` (`.view` con
  // transform): montar el overlay como hijo directo de `<body>`, fuera de cualquier ancestro que
  // aísle o reancle su apilamiento, sin tocar una sola clase CSS.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bulk-modal bulk-modal--wizard" onClick={(event) => event.stopPropagation()}>
        <button className="rec-modal-close" onClick={onClose} aria-label={t('common.close')}><i className="fas fa-times" /></button>
        <h2 className="bulk-title"><i className="fas fa-file-csv" /> {t('bulk.title')}</h2>
        <p className="bulk-sub">{t('bulk.subtitle')}</p>

        <div className="bulk-stepper" role="tablist" aria-label={t('bulk.title')}>
          {STEPS.map((wizardStep, index) => (
            <div
              key={wizardStep}
              className={`bulk-stepper-item ${index === stepIndex ? 'bulk-stepper-item--on' : ''} ${index < stepIndex ? 'bulk-stepper-item--done' : ''}`}
            >
              <span className="bulk-stepper-num">{index + 1}</span>
              <span className="bulk-stepper-label">{t(STEP_LABEL_KEYS[wizardStep])}</span>
            </div>
          ))}
        </div>
        <p className="bulk-step-of">{t('bulk.stepOf', { step: stepIndex + 1, total: STEPS.length })}</p>

        {step === 'instructions' && (
          <StepInstructions campaignId={campaignId} programa={programa} onNext={() => setStep('upload')} />
        )}
        {step === 'upload' && catalog.isError && (
          // El parseo del CSV depende del catálogo (una columna de marca por Fundamental/
          // Comportamiento/Habit): si la consulta falló, `fundamentals` quedaría en `[]` y CADA
          // fila del archivo fallaría en silencio con "El Fundamental es obligatorio" — indistinguible
          // de un archivo mal llenado. Mostrar el error real en vez de dejar avanzar al paso 2.
          <div className="bulk-step bulk-step--upload">
            <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {t('bulk.catalogError')}</p>
            <div className="bulk-wizard-nav">
              <button type="button" className="btn btn--secondary" onClick={() => setStep('instructions')}>
                <i className="fas fa-arrow-left" /> {t('bulk.back')}
              </button>
              <span />
            </div>
          </div>
        )}
        {step === 'upload' && !catalog.isError && catalog.isPending && (
          <div className="bulk-step bulk-step--upload">
            <p role="status" className="bulk-status-pending">{t('common.loading')}</p>
          </div>
        )}
        {step === 'upload' && !catalog.isError && !catalog.isPending && (
          <StepUploadFile
            fundamentals={fundamentals}
            onBack={() => setStep('instructions')}
            onParsed={(parsedRows) => { setRows(parsedRows); setStep('review'); }}
          />
        )}
        {step === 'review' && (
          <StepReviewRows
            rows={rows} campaignId={campaignId} programa={programa} fundamentals={fundamentals}
            onRowsChange={setRows}
            onBack={() => setStep('upload')}
            onNext={() => setStep('confirm')}
          />
        )}
        {step === 'confirm' && (
          <StepConfirm rows={rows} campaignId={campaignId} programa={programa} onBack={() => setStep('review')} onDone={onClose} />
        )}
      </div>
    </div>
  ), document.body);
}

export default BulkUploadModal;
